//@flow

import _ from 'lodash';
import { FEDERAL_SIGNIFICANCE_CITIES } from 'known-regions';
import { ACTUALITY_CODE, SECTION_LENGTHS, ACCURACIES } from './dictionaries';

//Формат КЛАДР: СС РРР ГГГ ППП УУУУ ДДДД
// СС    регион
// РРР   район
// ГГГ   город
// ППП   населенный пункт
// УУУУ  улица
// ДДДД  дом
// КЛАДРы до дома, могут оканчиваться на код актуальности длиной 2 символа.
// "1122233344499" - Кладр до населенного пункта, 99 - код актуальности.
// "11222333444555599" - Кладр до улицы, 99 - код актуальности.
// "1122233344455556666" - Кладр до дома.

function isValidSettlementKladr (kladr: string) {
	return _.isString(kladr) && kladr.length === ACCURACIES.SETTLEMENT_WITH_ACTUALITY;
}

function padToAccuracy (kladr: string, accuracy: number) {
	if (kladr && accuracy && _.isString(kladr)) {
		const padLength = accuracy - kladr.length;
		if (kladr.length < accuracy) {
			return `${kladr}${'0'.repeat(padLength)}`;
		}

		return kladr.slice(0, accuracy);
	}

	throw new Error('Invalid arguments');
}

function normalizeKladr (kladr: string) {
	if (_.isNumber(kladr)) {
		kladr = _.toString(kladr);

		// Из-за того, что старые кладры хранятся в числовом формате,
		// для регионов, начинающихся с 0 (например, 05 - Дагестан),
		// этот 0 исчезает и кладр из 15-значного превращается в 14-значный
		// и лидирующий "0" необходимо вернуть
		switch (kladr.length) {
			case 14:
				kladr = `0${kladr}`;
				break;
			default:
				_.noop();
		}
	}

	return kladr;
}

function getRegionCode (kladr: string): string {
	const code = kladr.slice(0, ACCURACIES.REGION);
	return code.length < ACCURACIES.REGION ? '' : code;
}

function getKladrFromDistrictToSettlement (kladr: string): string {
	const code = kladr.slice(ACCURACIES.REGION, ACCURACIES.SETTLEMENT);
	return code.length < (
		SECTION_LENGTHS.DISTRICT
		+ SECTION_LENGTHS.CITY
		+ SECTION_LENGTHS.SETTLEMENT) ? '' : code;
}

function getStreetCode (kladr: string): string {
	const code = kladr.slice(ACCURACIES.SETTLEMENT, ACCURACIES.STREET);
	return code.length < SECTION_LENGTHS.STREET ? '' : code;
}

function getHouseCode (kladr: string): string {
	const code = kladr.slice(ACCURACIES.STREET, ACCURACIES.HOUSE);
	return code.length < SECTION_LENGTHS.HOUSE ? '' : code;
}

function getActualityCodeFreeKladr (kladr: string): string {
	return getRegionCode(kladr)
		+ getKladrFromDistrictToSettlement(kladr)
		+ getStreetCode(kladr)
		+ getHouseCode(kladr);
}

function getKladrWithoutLastCode (kladr: string): string {
	const lastCode = getHouseCode(kladr)
		|| getStreetCode(kladr)
		|| getKladrFromDistrictToSettlement(kladr);

	const index = kladr.lastIndexOf(lastCode);

	return index > 0 ? kladr.slice(0, index) : kladr;
}

const extendedKladrUtils = {
	getRegionCode,
	isValidSettlementKladr,

	normalizeSettlementKladr: (kladr: ?(string | number)) => {
		kladr = normalizeKladr(kladr);

		return kladr && `${padToAccuracy(kladr, ACCURACIES.SETTLEMENT)}${ACTUALITY_CODE}`;
	},

	normalizeStreetKladr: (kladr: ?(string | number)) => {
		kladr = normalizeKladr(kladr);

		return kladr && `${padToAccuracy(kladr, ACCURACIES.STREET)}${ACTUALITY_CODE}`;
	},

	normalizeHouseKladr: (kladr: ?(string | number)) => {
		kladr = normalizeKladr(kladr);

		return kladr && padToAccuracy(kladr, ACCURACIES.HOUSE);
	},

	contains: (haystack: string, needle: string): boolean => {
		if (!haystack || !needle) {
			return false;
		}

		haystack = getActualityCodeFreeKladr(haystack);
		needle = getActualityCodeFreeKladr(needle);

		return needle.startsWith(haystack);
	},

	directlyContains: (haystack: string, needle: string): boolean => {
		if (!haystack || !needle) {
			return false;
		}

		haystack = getActualityCodeFreeKladr(haystack);
		needle = getActualityCodeFreeKladr(needle);

		if (haystack !== needle) {
			needle = getKladrWithoutLastCode(needle);

			while (haystack.length < needle.length) {
				haystack += '0';
			}
		}

		return haystack === needle;
	},

	isFederalSettlement: (kladr: string) => {
		const regionId = getRegionCode(kladr);
		return FEDERAL_SIGNIFICANCE_CITIES.includes(regionId);
	},
};

export default extendedKladrUtils;
